/**
 * Team and membership documents as they are now, read by id, for the dialogs the
 * Team tables open.
 *
 * A Team table's rows come from the Postgres mirror, and each renders a stand-in
 * built from its Postgres row until its live document arrives (LiveDoc). So a
 * row's own copy can be seconds old, or missing fields Postgres never has: a
 * Team's budget settings are never in the stand-in. A dialog that edits a Team or
 * a membership must start from Firestore, never from the row it was opened on,
 * or saving would write the stand-in's blanks over the real values.
 */
import { Team, TeamMember } from '@run-diffusion/shared';
import { db } from '@/firebase';

export async function fetchTeamsById (teamIds: string[]): Promise<Team[]> {
	const snapshots = await Promise.all(teamIds.map((teamId: string) => db.doc(`teams/${teamId}`).get()));
	return snapshots
		.filter((snapshot) => snapshot.exists)
		.map((snapshot) => ({ ...(snapshot.data() as Team), id: snapshot.id }));
}

export async function fetchTeamMembersByKey (teamId: string, keys: string[]): Promise<TeamMember[]> {
	const snapshots = await Promise.all(keys.map((key: string) => db.doc(`teams/${teamId}/teamMembers/${key}`).get()));
	return snapshots
		.filter((snapshot) => snapshot.exists)
		.map((snapshot) => ({ ...(snapshot.data() as TeamMember), id: snapshot.id }));
}

type RowPayload = Record<string, unknown>;

function teamIdOf (team: unknown): string | null {
	return team && typeof team === 'object' && typeof (team as { id?: unknown }).id === 'string'
		? (team as { id: string }).id
		: null;
}

/**
 * A row action's payload with every Team in it read fresh: the `teams` list, and
 * the single `team` or `duplicateTeam`. Null when any of them is gone.
 */
export async function withFreshTeams (payload: RowPayload): Promise<RowPayload | null> {
	const fresh: RowPayload = { ...payload };
	if (Array.isArray(payload.teams)) {
		const ids: string[] = payload.teams.map(teamIdOf).filter((id: string | null): id is string => !!id);
		const teams: Team[] = await fetchTeamsById(ids);
		if (!ids.length || teams.length !== ids.length) return null;
		fresh.teams = teams;
	}
	for (const key of ['team', 'duplicateTeam']) {
		const id: string | null = teamIdOf(payload[key]);
		if (!id) continue;
		const [team] = await fetchTeamsById([id]);
		if (!team) return null;
		fresh[key] = team;
	}
	return fresh;
}

/** A row action's payload with its `teamMembers` read fresh from `teamId`. Null when any is gone. */
export async function withFreshTeamMembers (teamId: string, payload: RowPayload): Promise<RowPayload | null> {
	if (!Array.isArray(payload.teamMembers)) return payload;
	const keys: string[] = payload.teamMembers.map(teamIdOf).filter((id: string | null): id is string => !!id);
	const teamMembers: TeamMember[] = await fetchTeamMembersByKey(teamId, keys);
	if (!keys.length || teamMembers.length !== keys.length) return null;
	return { ...payload, teamMembers };
}
